import {
  bigint,
  boolean,
  foreignKey,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
  unique,
  date,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { driver } from '@trashlab/api/src/domains/driver/schemas/driver';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { service_line } from '@trashlab/api/src/domains/orders/schemas/service-line';
import { InferQueryModel } from '@trashlab/api/src/types/drizzle-type-helpers';
import type { EligibleDateRange } from '@trashlab/api/src/domains/orders/shared/month-eligibility';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';

export const recurrence_level = pgEnum('recurrence_level', ['SERVICE', 'SITE']);

// Enum for denormalized recurrence frequency
export const recurrence_frequency = pgEnum('recurrence_frequency', [
  'WEEKLY',
  'MONTHLY',
]);

export const recurring_service = pgTable(
  'recurring_service',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    customerSiteId: uuid('customerSiteId'),
    createdBy: uuid('createdBy'),
    recurrenceLevel: recurrence_level('recurrenceLevel')
      .default('SERVICE')
      .notNull(),
    serviceLineId: uuid('serviceLineId'),
    /** @deprecated Use rs_frequency, rs_interval, rs_bysetpos_arr, rs_sun-rs_sat columns instead. */
    recurrenceRule: text('recurrenceRule').notNull(),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    defaultDriverId: uuid('defaultDriverId'),
    sequenceNumber: integer('sequenceNumber'),
    skipRecurrenceOn: date('skipRecurrenceOn'),
    // Denormalized recurrence columns for SQL-level filtering
    rs_frequency: recurrence_frequency('rs_frequency'),
    rs_interval: integer('rs_interval'),
    rs_dtstart: timestamp('rs_dtstart', { withTimezone: true }),
    /** @deprecated Use rs_bysetpos_arr instead */
    rs_bysetpos: integer('rs_bysetpos'), // DEPRECATED: For MONTHLY: 1=1st, 2=2nd, 3=3rd, 4=4th, -1=last
    rs_bysetpos_arr: integer('rs_bysetpos_arr')
      .array()
      .default(sql`'{}'::integer[]`), // For MONTHLY: [1]=1st, [2]=2nd, [3]=3rd, [4]=4th, [-1]=last; supports multiple e.g. [1,3] for 1st and 3rd
    // Boolean day columns for efficient indexing
    rs_sun: boolean('rs_sun').default(false),
    rs_mon: boolean('rs_mon').default(false),
    rs_tue: boolean('rs_tue').default(false),
    rs_wed: boolean('rs_wed').default(false),
    rs_thu: boolean('rs_thu').default(false),
    rs_fri: boolean('rs_fri').default(false),
    rs_sat: boolean('rs_sat').default(false),
    // Eligible date range for seasonal services (copied from recurrence_settings at order creation)
    eligibleStartDate: date('eligibleStartDate'),
    eligibleEndDate: date('eligibleEndDate'),
    eligibleDateRanges:
      jsonb('eligibleDateRanges').$type<EligibleDateRange[]>(),
  },
  (table) => {
    return {
      site_merge_page_idx: index('recurring_service_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      createdAtTs_idx: index('recurring_service_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('recurring_service_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('recurring_service_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('recurring_service_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('recurring_service_versionNumber_idx').on(
        table.versionNumber,
      ),
      customerSiteId_idx: index('recurring_service_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      defaultDriverId_idx: index('recurring_service_defaultDriverId_idx').on(
        table.defaultDriverId,
      ),
      serviceLineId_idx: index('recurring_service_serviceLineId_idx').on(
        table.serviceLineId,
      ),

      //foreign keys
      recurring_service_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'recurring_service_customerSiteId_fkey',
      }),
      recurring_service_defaultDriverId_fkey: foreignKey({
        columns: [table.defaultDriverId],
        foreignColumns: [driver.id],
        name: 'recurring_service_defaultDriverId_fkey',
      }),
      recurring_service_serviceLineId_fkey: foreignKey({
        columns: [table.serviceLineId],
        foreignColumns: [service_line.id],
        name: 'recurring_service_serviceLineId_fkey',
      }),
      uniqueCustomerSiteIdAndServiceLineId: unique(
        'uniqueCustomerSiteIdAndServiceLineId',
      ).on(table.customerSiteId, table.serviceLineId),
      // Composite indexes for tenantId + day filtering (used by getUnassignedStops)
      rs_sun_idx: index('recurring_service_rs_sun_idx').on(
        table.tenantId,
        table.rs_sun,
      ),
      rs_mon_idx: index('recurring_service_rs_mon_idx').on(
        table.tenantId,
        table.rs_mon,
      ),
      rs_tue_idx: index('recurring_service_rs_tue_idx').on(
        table.tenantId,
        table.rs_tue,
      ),
      rs_wed_idx: index('recurring_service_rs_wed_idx').on(
        table.tenantId,
        table.rs_wed,
      ),
      rs_thu_idx: index('recurring_service_rs_thu_idx').on(
        table.tenantId,
        table.rs_thu,
      ),
      rs_fri_idx: index('recurring_service_rs_fri_idx').on(
        table.tenantId,
        table.rs_fri,
      ),
      rs_sat_idx: index('recurring_service_rs_sat_idx').on(
        table.tenantId,
        table.rs_sat,
      ),
    };
  },
);

export const recurring_service_history = pgTable('recurring_service_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  customerSiteId: uuid('customerSiteId'),
  createdBy: uuid('createdBy'),
  recurrenceLevel: recurrence_level('recurrenceLevel'),
  serviceLineId: uuid('serviceLineId'),
  recurrenceRule: text('recurrenceRule'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  defaultDriverId: uuid('defaultDriverId'),
  sequenceNumber: integer('sequenceNumber'),
  skipRecurrenceOn: date('skipRecurrenceOn'),
  // Denormalized recurrence columns for SQL-level filtering
  rs_frequency: recurrence_frequency('rs_frequency'),
  rs_interval: integer('rs_interval'),
  rs_dtstart: timestamp('rs_dtstart', { withTimezone: true }),
  /** @deprecated Use rs_bysetpos_arr instead */
  rs_bysetpos: integer('rs_bysetpos'), // DEPRECATED: For MONTHLY: 1=1st, 2=2nd, 3=3rd, 4=4th, -1=last
  rs_bysetpos_arr: integer('rs_bysetpos_arr').array(), // For MONTHLY: [1]=1st, [2]=2nd, [3]=3rd, [4]=4th, [-1]=last; supports multiple e.g. [1,3] for 1st and 3rd
  // Boolean day columns for efficient indexing
  rs_sun: boolean('rs_sun'),
  rs_mon: boolean('rs_mon'),
  rs_tue: boolean('rs_tue'),
  rs_wed: boolean('rs_wed'),
  rs_thu: boolean('rs_thu'),
  rs_fri: boolean('rs_fri'),
  rs_sat: boolean('rs_sat'),
  // Eligible date range for seasonal services
  eligibleStartDate: date('eligibleStartDate'),
  eligibleEndDate: date('eligibleEndDate'),
  eligibleDateRanges: jsonb('eligibleDateRanges').$type<EligibleDateRange[]>(),
});

export type RecurringServiceSchema = typeof recurring_service.$inferSelect;

export type ListUnassignedStopsResponse = Omit<
  InferQueryModel<
    'recurring_service',
    {
      columns: {
        id: true;
        recurrenceRule: true;
        defaultDriverId: true;
        serviceLineId: true;
        skipRecurrenceOn: true;
        // Denormalized recurrence columns (backfill complete; preferred over parsing recurrenceRule)
        rs_frequency: true;
        rs_interval: true;
        rs_bysetpos: true; // @deprecated - use rs_bysetpos_arr
        rs_bysetpos_arr: true;
        rs_sun: true;
        rs_mon: true;
        rs_tue: true;
        rs_wed: true;
        rs_thu: true;
        rs_fri: true;
        rs_sat: true;
        // Eligible date range for seasonal services
        eligibleStartDate: true;
        eligibleEndDate: true;
        eligibleDateRanges: true;
      };
      with: {
        customerSite: {
          columns: {
            id: true;
            name: true;
            address: true;
            addressString: true;
            latitude: true;
            longitude: true;
            notes: true;
          };
          with: {
            customer: {
              columns: {
                id: true;
                name: true;
              };
            };
          };
        };
        serviceLineCanonical: {
          columns: {
            id: true;
            deliveryDate: true;
            pickupDate: true;
            lastServiceDate: true;
            status: true;
          };
          with: {
            material: {
              columns: {
                id: true;
                name: true;
              };
            };
            product: {
              columns: {
                containerType: true;
                cubicYardage: true;
              };
            };
            asset: {
              columns: {
                id: true;
                userFacingId: true;
              };
            };
          };
        };
      };
    }
  >,
  'skipRecurrenceOn'
>;
